import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { Provider, ProviderCategories } from '../../../../types';
import { toast } from 'react-toastify';
import { getProviderByProviderId, getAllProviderCategoriesByCompanyId } from '../../../../../../../../crud/management_api/providers';

interface AssignCategoryProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  provider: Provider;
  onSuccess: (updatedProvider: Provider) => void;
}

const AssignCategory: React.FC<AssignCategoryProps> = ({
  showModal,
  setShowModal,
  provider,
  onSuccess
}) => {
  const [allCategories, setAllCategories] = useState<ProviderCategories[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAvailableCategories = useCallback(async () => {
    try {
      const userContext = localStorage.getItem('user');
      const companyId = userContext ? JSON.parse(userContext).company_id : 0;
      
      const response: any = await getAllProviderCategoriesByCompanyId(companyId);
      const allCats = response.data || [];
      
      // Filter out categories already assigned to this provider
      const assignedCategoryIds = provider.categories?.map(c => Number(c.category_id)) || [];
      const availableCats = allCats.filter((cat: ProviderCategories) => 
        !assignedCategoryIds.includes(Number(cat.category_id))
      );
      
      setAllCategories(availableCats);
    } catch (error) {
      console.error('Error al obtener categorías:', error);
      toast.error('Error al cargar las categorías disponibles');
    }
  }, [provider.categories]);

  useEffect(() => {
    if (showModal) {
      fetchAvailableCategories();
    }
  }, [showModal, fetchAvailableCategories]);

  const handleToggleCategory = (categoryId: number) => {
    setSelectedCategoryIds(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleAssign = async () => {
    if (selectedCategoryIds.length === 0) {
      toast.warning('Por favor selecciona al menos una categoría');
      return;
    }

    setLoading(true);
    try {
      // Import the assign function dynamically to avoid circular dependencies
      const { assignCategoriesToProvider } = await import('../../../../../../../../crud/management_api/providers');
      
      await assignCategoriesToProvider(Number(provider.provider_id), selectedCategoryIds);
      
      const categoryNames = allCategories
        .filter(cat => selectedCategoryIds.includes(Number(cat.category_id)))
        .map(cat => cat.name)
        .join(', ');
      
      toast.success(`${selectedCategoryIds.length > 1 ? 'Categorías asignadas' : 'Categoría asignada'} correctamente: ${categoryNames}`);
      
      // Fetch updated provider data
      const response: any = await getProviderByProviderId(String(provider.provider_id));
      onSuccess(response.data);
      setShowModal(false);
      setSelectedCategoryIds([]);
    } catch (error: any) {
      console.error('Error al asignar categorías:', error);
      toast.error(error?.response?.data?.message || 'Error al asignar las categorías');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={showModal} onHide={() => setShowModal(false)} centered>
      <Modal.Header>
        <div className='form-modal-title-container'>
          <Modal.Title className='form-modal-title'>
            Asignar categorías a {provider.name}
          </Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={() => setShowModal(false)}>
          <X size={24} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        {allCategories.length === 0 ? (
          <div className="no-data-available-message">
            No hay categorías disponibles para asignar. Todas las categorías ya están asignadas a este proveedor.
          </div>
        ) : (
          <div className="category-selection-container">
            <p className="category-selection-title">Selecciona las categorías que deseas asignar:</p>
            <div className="category-badge-list">
              {allCategories.map(category => (
                <button
                  key={category.category_id}
                  className={`category-selection-badge ${
                    selectedCategoryIds.includes(Number(category.category_id)) ? 'selected' : ''
                  }`}
                  onClick={() => handleToggleCategory(Number(category.category_id))}
                  type="button"
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button className="form-button-cancel" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button 
            className="form-button-create" 
            onClick={handleAssign}
            disabled={selectedCategoryIds.length === 0 || loading}
          >
            {loading ? 'Asignando...' : `Asignar (${selectedCategoryIds.length})`}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignCategory;
